/* eslint no-case-declarations: off */
import { FC, useMemo, useState } from 'react';
import { Check, Pencil, Plus, X } from 'lucide-react';
import clsx from 'clsx';

import {
  parseGradient,
  getHexAlpha,
  rgbaToArray,
  rgbaToHex
} from '../../../utils';
import {
  MAX_CORES_SALVAS,
  gravarCores,
  lerCores
} from '../../../utils/coresSalvas';
import { checkValidColorsArray } from '../../Colorpicker/helper';
import { useTranslation } from '../../../../../../lib/i18n/TranslationContext';

import { IColor } from '../GradientPanel/types';
import { IActiveColor } from '../types';

const PADRAO_VISIVEIS = 5;

const brutaDe = (item: string | IColor) =>
  typeof item === 'string' ? item : item.gradient;

interface IProps {
  defaultColors?: Array<string>;
  setColor: (color: any) => void;
  setInit: (init: boolean) => void;
  setActiveColor?: (color: IActiveColor) => void;
  onDefaultColorSelect?: (color: string | IColor) => void;
  colorType: 'solid' | 'gradient';
  corAtual?: string;
}

const DefaultColorPanel: FC<IProps> = ({
  defaultColors = [],
  setColor,
  setActiveColor,
  onDefaultColorSelect,
  setInit,
  colorType,
  corAtual
}) => {
  const { t } = useTranslation();

  const [active, setActive] = useState<number>(-1);
  const [salvas, setSalvas] = useState<string[]>(() =>
    lerCores('salvas', colorType)
  );
  const [ocultas, setOcultas] = useState<string[]>(() =>
    lerCores('ocultas', colorType)
  );
  const [editando, setEditando] = useState(false);

  const formatar = (brutas: string[]) =>
    colorType === 'gradient'
      ? checkValidColorsArray(brutas, 'grad').map((item: string) =>
          parseGradient(item)
        )
      : checkValidColorsArray(brutas, 'solid');

  const padrao = useMemo(
    () =>
      formatar(defaultColors.slice(0, PADRAO_VISIVEIS)).filter(
        (item: string | IColor) => !ocultas.includes(brutaDe(item))
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultColors, colorType, ocultas]
  );

  const guardadas = useMemo(
    () =>
      formatar(salvas).filter(
        (item: string | IColor) =>
          !padrao.some(
            (visivel: string | IColor) => brutaDe(visivel) === brutaDe(item)
          )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [salvas, padrao, colorType]
  );

  const cheio = salvas.length >= MAX_CORES_SALVAS;
  const jaExibida =
    !!corAtual &&
    [...padrao, ...guardadas].some((item) => brutaDe(item) === corAtual);
  const podeSalvar = !!corAtual && !jaExibida && !cheio;

  const salvarCor = () => {
    if (!podeSalvar || !corAtual) return;

    const novas = [...salvas, corAtual];
    setSalvas(novas);
    gravarCores('salvas', colorType, novas);
  };

  const excluirCor = (cor: string, ehPadrao: boolean) => {
    if (ehPadrao) {
      const novas = [...ocultas, cor];
      setOcultas(novas);
      gravarCores('ocultas', colorType, novas);
    } else {
      const novas = salvas.filter((item) => item !== cor);
      setSalvas(novas);
      gravarCores('salvas', colorType, novas);
    }

    setActive(-1);
    if (padrao.length + guardadas.length <= 1) setEditando(false);
  };

  const onChooseColor = (item: string | IColor, index: number) => {
    if (index === active) {
      return;
    }

    if (colorType === 'gradient' && typeof item !== 'string') {
      const { stops } = item;
      const lastStop = rgbaToArray(stops[stops.length - 1][0]);
      const lastStopLoc = stops[stops.length - 1][1];
      const activeStop = rgbaToHex([lastStop[0], lastStop[1], lastStop[2]]);
      const activeIdx = stops[stops.length - 1][2];

      setInit(false);

      onDefaultColorSelect?.(item?.gradient);
      setColor(item);
      setActiveColor &&
        setActiveColor({
          hex: activeStop,
          alpha: Number(Math.round(lastStop[3] * 100)),
          loc: lastStopLoc,
          index: activeIdx
        });
      setActive(index);
    } else if (colorType !== 'gradient' && typeof item === 'string') {
      setInit(false);
      onDefaultColorSelect?.(item);
      setColor(getHexAlpha(item));
      setActive(index);
    }
  };

  if (!Array.isArray(defaultColors) || (!defaultColors.length && !salvas.length)) {
    return null;
  }

  const grade =
    'mx-[-16px] mt-[14px] grid max-h-[105px] grid-cols-[repeat(auto-fill,30px)] justify-between gap-1.5 overflow-auto px-4 pb-px pt-0.5';

  const swatch =
    'relative size-[30px] cursor-pointer select-none rounded-md outline-none shadow-[0_0_0_1px_rgba(17,24,39,0.12)] transition-transform duration-[120ms] active:scale-[0.94] motion-reduce:transition-none motion-reduce:active:scale-100';

  const botao =
    'flex size-[30px] items-center justify-center rounded-md border border-dashed border-gray-300 text-gray-400 transition-colors duration-[120ms] hover:border-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:text-gray-400';

  const marcaAtiva =
    'absolute inset-[35%] rounded-full bg-white shadow-[0_1px_2px_0_rgba(0,0,0,0.38)]';

  const capaExcluir =
    'absolute inset-0 flex items-center justify-center rounded-md bg-gray-900/60 text-white';

  const tituloSalvar = cheio
    ? `${t('Limite de cores salvas atingido')} (${MAX_CORES_SALVAS})`
    : jaExibida
      ? t('Cor já salva')
      : t('Salvar cor');

  const renderSwatch = (
    item: string | IColor,
    index: number,
    ehPadrao: boolean
  ) => {
    const bruta = brutaDe(item);

    return (
      <div
        key={bruta + index}
        onClick={() =>
          editando ? excluirCor(bruta, ehPadrao) : onChooseColor(item, index)
        }
        title={editando ? t('Excluir cor') : undefined}
        className={swatch}
        style={{
          background: bruta,
          boxShadow:
            colorType !== 'gradient' && active === index
              ? `${bruta} 0px 0px 4px`
              : undefined
        }}
      >
        {editando ? (
          <div className={capaExcluir}>
            <X className='size-3.5' />
          </div>
        ) : (
          active === index && <div className={marcaAtiva}></div>
        )}
      </div>
    );
  };

  return (
    <div className={grade}>
      {padrao.map((item: string | IColor, index: number) =>
        renderSwatch(item, index, true)
      )}

      {guardadas.map((item: string | IColor, index: number) =>
        renderSwatch(item, padrao.length + index, false)
      )}

      <button
        type='button'
        onClick={salvarCor}
        disabled={!podeSalvar}
        title={tituloSalvar}
        aria-label={tituloSalvar}
        className={botao}
      >
        <Plus className='size-4' />
      </button>

      {!!(padrao.length + guardadas.length) && (
        <button
          type='button'
          onClick={() => setEditando((atual) => !atual)}
          title={editando ? t('Concluir') : t('Editar cores')}
          aria-label={editando ? t('Concluir') : t('Editar cores')}
          className={clsx(botao, editando && 'border-solid border-gray-900 text-gray-900')}
        >
          {editando ? <Check className='size-4' /> : <Pencil className='size-3.5' />}
        </button>
      )}
    </div>
  );
};

export default DefaultColorPanel;
